/**
 * Minimal SVG bar/line charts for the Sales Reports screen. Deliberately not a charting library -
 * MASTER_PROMPT scope note for this screen says "basic charts are fine". Local equivalent of
 * whatever chart primitive @loctopus/ui eventually ships; promote/replace when it does.
 */

export interface ChartPoint {
  label: string;
  value: number;
}

const WIDTH = 640;
const HEIGHT = 220;
const PADDING = 32;

export function BarChart({ points, color = '#2B3A55' }: { points: ChartPoint[]; color?: string }) {
  const max = Math.max(1, ...points.map((p) => p.value));
  const barWidth = points.length > 0 ? (WIDTH - PADDING * 2) / points.length : 0;

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="w-full h-auto"
      role="img"
      aria-label="Bar chart"
    >
      <line
        x1={PADDING}
        y1={HEIGHT - PADDING}
        x2={WIDTH - PADDING}
        y2={HEIGHT - PADDING}
        stroke="#E2E8F0"
      />
      {points.map((p, i) => {
        const h = ((HEIGHT - PADDING * 2) * p.value) / max;
        const x = PADDING + i * barWidth + barWidth * 0.15;
        const w = barWidth * 0.7;
        const y = HEIGHT - PADDING - h;
        return (
          <g key={p.label}>
            <rect x={x} y={y} width={w} height={h} fill={color} rx={2}>
              <title>{`${p.label}: ${p.value}`}</title>
            </rect>
            <text
              x={x + w / 2}
              y={HEIGHT - PADDING + 14}
              fontSize={9}
              textAnchor="middle"
              fill="#64748B"
            >
              {p.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function LineChart({ points, color = '#7B5EA7' }: { points: ChartPoint[]; color?: string }) {
  const max = Math.max(1, ...points.map((p) => p.value));
  const stepX = points.length > 1 ? (WIDTH - PADDING * 2) / (points.length - 1) : 0;

  const coords = points.map((p, i) => {
    const x = PADDING + i * stepX;
    const y = HEIGHT - PADDING - ((HEIGHT - PADDING * 2) * p.value) / max;
    return { x, y, p };
  });
  const path = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x},${c.y}`).join(' ');

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="w-full h-auto"
      role="img"
      aria-label="Line chart"
    >
      <line
        x1={PADDING}
        y1={HEIGHT - PADDING}
        x2={WIDTH - PADDING}
        y2={HEIGHT - PADDING}
        stroke="#E2E8F0"
      />
      <path d={path} fill="none" stroke={color} strokeWidth={2} />
      {coords.map((c) => (
        <g key={c.p.label}>
          <circle cx={c.x} cy={c.y} r={3} fill={color}>
            <title>{`${c.p.label}: ${c.p.value}`}</title>
          </circle>
          <text x={c.x} y={HEIGHT - PADDING + 14} fontSize={9} textAnchor="middle" fill="#64748B">
            {c.p.label}
          </text>
        </g>
      ))}
    </svg>
  );
}
